import { supabase } from '@/lib/supabase';

/**
 * Finished-PC stock for one build. Mirrors public.build_stock().
 *
 * Every field except `on_hand` and `available` is always a number. Those two
 * are null for a build whose opening balance was never set - which is not the
 * same as none in stock, and is rendered as a dash rather than a zero.
 */
export interface BuildStock {
  build_id: string;
  sku: string;
  /** Units per Business Central at `opening_at`. Null means never synced. */
  opening_qty: number | null;
  opening_at: string | null;
  /** Units from assembly orders completed strictly after `opening_at`. */
  built_since: number;
  /** Net units sold after `opening_at`. Signed, so credit notes reduce it. */
  sold_since: number;
  /** opening + built - sold. Null when never synced. */
  on_hand: number | null;
  /** Machines of this build standing on a demo station, whatever its status. */
  on_display: number;
  /** on_hand - on_display. Null when never synced. */
  available: number | null;
}

/**
 * Stock for every build, keyed by build id.
 *
 * Through the RPC rather than reading `builds` directly, because the figure is
 * derived: the stored column is only the opening balance, and what anyone
 * wants to see is that balance plus everything built and sold since. Computing
 * it in SQL also keeps sales_entries - which is admin-only - out of the
 * browser, while still letting every role see a count.
 */
export async function fetchBuildStock(): Promise<Map<string, BuildStock>> {
  const { data, error } = await supabase.rpc('build_stock');
  if (error) throw error;

  const byBuild = new Map<string, BuildStock>();
  (data ?? []).forEach((row: BuildStock) => byBuild.set(row.build_id, row));
  return byBuild;
}
